import React, { useEffect, useState } from 'react'
import Header from 'components/layout/Header'
import Sidebar from 'components/layout/Sidebar'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import useAuth from 'utils/hooks/useAuth'
import { getAddonData, getBranchData, getCompanyData, getPermissionData, getRoleData } from 'store/data/dataSlice'
import { getApiProfile } from 'services/AuthService'
import { setUser } from 'store/auth/userSlice'
import Breadcrumbs from 'components/Ui/Breadcrumbs'
import ErrorBoundary from 'components/ErrorBoundary'
import OfflineBanner from 'components/OfflineBanner'

const OVERRIDE_LABELS = {
    'all-branches': 'All Branches',
    'branch-timings': 'Timings',
    'assign-permissions': 'Permissions',
    'ratings-reviews': 'Ratings & Reviews',
    'inquiry': 'Inquiries',
    'nfc-cards': 'NFC Cards',
    'advanced-analytics': 'Advanced Analytics',
    'manage-product-subscription': 'Manage Product Subscription',
    'product-categories': 'Product Categories',
    'all-products': 'All Products',
    'product-subs': 'Product Subs',
    'assign-subscription': 'Assign Subscription',
}

function humanise(seg) {
    if (OVERRIDE_LABELS[seg]) return OVERRIDE_LABELS[seg]
    return seg
        .split('-')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ')
}

function getPathBreadcrumbs(pathname) {
    const segments = pathname.split('/').filter(Boolean)
    const crumbs = []
    let cumulative = ''
    for (const seg of segments) {
        cumulative += '/' + seg
        const label = humanise(seg)
        if (seg === 'edit' || seg === 'create') {
            crumbs.push({ label })
        } else {
            crumbs.push({ label, link: cumulative })
        }
    }
    return crumbs
}

export default function AppLayout() {
    const { authenticated, checkPermission } = useAuth()
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()
    const userInfo = useSelector(state => state.auth.user)

    useEffect(() => {
        if (!authenticated) {
            navigate('/login')
            return
        }
        const wildcard = location.pathname.split('/').filter(Boolean)
        const last = wildcard[wildcard.length - 1]
        const allowed =
            location.pathname === '/dashboard' ||
            location.pathname.includes('profile') ||
            checkPermission(last) ||
            checkPermission(wildcard[0], last) ||
            checkPermission(wildcard[1], last)
        if (!allowed) {
            navigate('/dashboard')
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname])

    useEffect(() => {
        if (authenticated) {
            dispatch(getCompanyData())
            dispatch(getBranchData())
            dispatch(getRoleData())
            dispatch(getPermissionData())
            dispatch(getAddonData())
            fetchData()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, authenticated])

    const fetchData = () => {
        getApiProfile()
            .then(response => {
                const user = {
                    ...userInfo,
                    email: response.data.email,
                }
                dispatch(setUser(user))
            })
            .catch(error => console.log('Error: ', error))
    }

    const breadcrumbs = getPathBreadcrumbs(location.pathname)
    const hasPageBreadcrumbs = /^\/(loyalty(\/(cafes|advanced-analytics|notifications|settings|tiers))?)(\/|$)/.test(location.pathname)

    return (
        <div className="dark:bg-boxdark2 dark:text-bodydark">
            <div className="flex h-screen overflow-hidden">
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    <OfflineBanner />
                    <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                    <main className="border-t border-primary/10">
                        <div className="p-4 mx-auto max-w-screen-2xl md:p-5 2xl:p-8">
                            {!hasPageBreadcrumbs && breadcrumbs.length > 0 && (
                                <Breadcrumbs items={breadcrumbs} />
                            )}
                            <ErrorBoundary>
                                <Outlet />
                            </ErrorBoundary>
                        </div>
                    </main>
                </div>
            </div>
        </div>
    )
}
